// Reusable DEV CONTROLS panel — the single home for all tuning knobs and dev UI.
//  • a scrollable menu of categorized "banks" (collapsible groups), each openable/closeable independently;
//  • sliders pair a range with a NUMBER input (accepts values OUTSIDE the slider range) + a reset button;
//  • also buttons, toggles, text/number inputs, and live readouts — so whole control panels fold in here;
//  • slider values + open/closed state persist to localStorage, so dev tweaks survive reloads.
//
// After anything that REBUILDS a slider's target (e.g. the terra material on regenerate), call
// `dev.applyAll()` to re-push every current slider value onto the fresh object.

export interface SliderSpec {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number; // default (used if no persisted value; also the reset target)
  onChange: (v: number) => void;
}

interface Control {
  apply(): void;      // re-fire onChange with the current value (no-op for regenerate-driving controls)
  snapshot(): string; // "label = value" line for copy/export
}

const LS_PREFIX = "lfdev:";
const lsGet = (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string): void => { try { localStorage.setItem(k, v); } catch { /* ignore */ } };
const lsDel = (k: string): void => { try { localStorage.removeItem(k); } catch { /* ignore */ } };

function copyText(text: string): void {
  const fallback = (): void => {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.cssText = "position:fixed;opacity:0;pointer-events:none";
    document.body.appendChild(ta); ta.select();
    try { document.execCommand("copy"); } catch { /* ignore */ }
    ta.remove();
  };
  try { navigator.clipboard?.writeText ? navigator.clipboard.writeText(text).catch(fallback) : fallback(); }
  catch { fallback(); }
}
// a small "copy" button with a ✓ flash on click
function copyButton(get: () => string): HTMLButtonElement {
  const b = document.createElement("button");
  b.textContent = "copy"; b.title = "copy these values to the clipboard";
  b.style.cssText = "background:transparent;border:1px solid #2a3a44;color:#6b8b96;font:9px 'Courier New',monospace;padding:1px 5px;border-radius:3px;cursor:pointer;pointer-events:auto";
  b.addEventListener("click", (e) => {
    e.stopPropagation();
    copyText(get());
    const o = b.textContent; b.textContent = "✓"; setTimeout(() => { b.textContent = o; }, 900);
  });
  return b;
}

const INPUT_CSS = "background:#0c1116;border:1px solid #2a3038;color:#cfe6ee;font:11px 'Courier New',monospace;padding:2px 5px;border-radius:4px;pointer-events:auto";
const BTN_CSS = "background:#16202a;border:1px solid #2a3a44;color:#cfe6ee;font:11px 'Courier New',monospace;padding:3px 7px;border-radius:4px;cursor:pointer;pointer-events:auto";

export class DevBank {
  private controls: Control[] = [];
  constructor(private name: string, private body: HTMLDivElement) {}

  private rowEl(): HTMLDivElement {
    const r = document.createElement("div");
    r.style.cssText = "display:flex;gap:6px;align-items:center;margin-top:6px";
    return r;
  }
  private labelEl(text: string, w = ""): HTMLSpanElement {
    const s = document.createElement("span");
    s.textContent = text;
    s.style.cssText = `font-size:11px;color:#b8c0cc;${w ? `width:${w};` : "flex:1;"}overflow:hidden;text-overflow:ellipsis;white-space:nowrap`;
    return s;
  }

  addSlider(spec: SliderSpec): DevBank {
    const key = `${LS_PREFIX}${this.name}:${spec.label}`;
    const stored = lsGet(key);
    let value = stored != null && Number.isFinite(Number(stored)) ? Number(stored) : spec.value;

    const wrap = document.createElement("div");
    wrap.style.marginTop = "7px";
    const top = this.rowEl();
    top.style.marginTop = "0";
    const lab = this.labelEl(spec.label);
    const num = document.createElement("input");
    num.type = "number";
    num.step = String(spec.step);
    num.value = String(round(value));
    num.style.cssText = INPUT_CSS + ";width:58px;text-align:right;color:#9fe1cb";
    const reset = document.createElement("button");
    reset.textContent = "↺";
    reset.title = "reset to default";
    reset.style.cssText = "background:transparent;border:1px solid #2a3a44;color:#6b7683;font:10px monospace;padding:1px 4px;border-radius:3px;cursor:pointer;pointer-events:auto";
    top.append(lab, num, reset);

    const range = document.createElement("input");
    range.type = "range";
    range.min = String(spec.min);
    range.max = String(spec.max);
    range.step = String(spec.step);
    range.value = String(clamp(value, spec.min, spec.max));
    range.style.cssText = "width:100%;height:14px;cursor:pointer;accent-color:#3fd0e6;pointer-events:auto";

    const commit = (v: number, persist = true): void => {
      value = v;
      range.value = String(clamp(v, spec.min, spec.max)); // slider pins to its range
      num.value = String(round(v));                       // number shows the true (maybe out-of-range) value
      if (persist) lsSet(key, String(v));
      spec.onChange(v);
    };
    range.addEventListener("input", () => commit(parseFloat(range.value)));
    num.addEventListener("change", () => { const v = parseFloat(num.value); if (Number.isFinite(v)) commit(v); });
    reset.addEventListener("click", () => { lsDel(key); commit(spec.value, false); });

    wrap.append(top, range);
    this.body.appendChild(wrap);
    this.controls.push({ apply: () => spec.onChange(value), snapshot: () => `${spec.label} = ${round(value)}` });
    spec.onChange(value); // push initial (persisted-or-default) value
    return this;
  }

  /** Full-width button. */
  addButton(label: string, onClick: () => void, accent?: string): HTMLButtonElement {
    const b = document.createElement("button");
    b.textContent = label;
    b.style.cssText = BTN_CSS + ";width:100%;margin-top:6px" + (accent ? `;border-color:${accent};color:${accent}` : "");
    b.addEventListener("click", onClick);
    this.body.appendChild(b);
    return b;
  }

  /** A row of small buttons. */
  addButtons(specs: { label: string; onClick: () => void; accent?: string }[]): void {
    const row = this.rowEl();
    for (const s of specs) {
      const b = document.createElement("button");
      b.textContent = s.label;
      b.style.cssText = BTN_CSS + ";flex:1" + (s.accent ? `;border-color:${s.accent};color:${s.accent}` : "");
      b.addEventListener("click", s.onClick);
      row.appendChild(b);
    }
    this.body.appendChild(row);
  }

  /** A wrapped row of checkboxes. Returns the checkbox elements (for mutual-exclusion etc.). */
  addToggles(specs: { label: string; value: boolean; onChange: (v: boolean) => void }[]): HTMLInputElement[] {
    const row = this.rowEl();
    row.style.flexWrap = "wrap";
    const cbs: HTMLInputElement[] = [];
    for (const s of specs) {
      const l = document.createElement("label");
      l.style.cssText = "display:flex;gap:3px;align-items:center;cursor:pointer;font-size:11px;color:#b8c0cc;pointer-events:auto";
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = s.value;
      cb.style.pointerEvents = "auto";
      cb.addEventListener("change", () => s.onChange(cb.checked));
      l.append(cb, document.createTextNode(s.label));
      row.appendChild(l);
      this.controls.push({ apply: () => {}, snapshot: () => `${s.label} = ${cb.checked}` });
      cbs.push(cb);
    }
    this.body.appendChild(row);
    return cbs;
  }

  /** Text input with an optional adjacent button (e.g. seed + random). */
  addText(label: string, value: string, onChange: (v: string) => void, button?: { label: string; onClick: () => void }): HTMLInputElement {
    const row = this.rowEl();
    const inp = document.createElement("input");
    inp.type = "text";
    inp.value = value;
    inp.style.cssText = INPUT_CSS + ";flex:1;min-width:0";
    inp.addEventListener("change", () => onChange(inp.value));
    this.controls.push({ apply: () => {}, snapshot: () => `${label} = "${inp.value}"` });
    row.append(this.labelEl(label, "36px"), inp);
    if (button) {
      const b = document.createElement("button");
      b.textContent = button.label;
      b.style.cssText = BTN_CSS;
      b.addEventListener("click", button.onClick);
      row.appendChild(b);
    }
    this.body.appendChild(row);
    return inp;
  }

  /** Number input with −/+ steppers (for unbounded integers like level). */
  addStepper(label: string, value: number, onChange: (v: number) => void, opts: { min?: number; step?: number } = {}): { set(v: number): void } {
    const min = opts.min ?? 1, step = opts.step ?? 1;
    const row = this.rowEl();
    const inp = document.createElement("input");
    inp.type = "number";
    inp.value = String(value);
    inp.style.cssText = INPUT_CSS + ";width:52px;text-align:center";
    let cur = value;
    const apply = (v: number): void => { cur = Math.max(min, v); inp.value = String(cur); onChange(cur); };
    inp.addEventListener("change", () => { const v = Math.floor(Number(inp.value)); apply(Number.isFinite(v) ? v : min); });
    const dec = document.createElement("button"); dec.textContent = "−"; dec.style.cssText = BTN_CSS;
    const inc = document.createElement("button"); inc.textContent = "+"; inc.style.cssText = BTN_CSS;
    dec.addEventListener("click", () => apply(cur - step));
    inc.addEventListener("click", () => apply(cur + step));
    row.append(this.labelEl(label, "36px"), dec, inp, inc);
    this.body.appendChild(row);
    this.controls.push({ apply: () => {}, snapshot: () => `${label} = ${cur}` });
    return { set: (v: number) => { cur = v; inp.value = String(v); } };
  }

  /** A live text readout; returns a setter. */
  addReadout(mono = false): (text: string, color?: string) => void {
    const d = document.createElement("div");
    d.style.cssText = `margin-top:6px;font-size:11px;color:#9fe1cb;${mono ? "white-space:pre-wrap;font-family:'Courier New',monospace;" : ""}`;
    this.body.appendChild(d);
    return (text: string, color?: string) => { d.textContent = text; if (color) d.style.color = color; };
  }

  applyAll(): void { for (const c of this.controls) c.apply(); }

  /** All value controls as `label = value` lines under a `[Bank Name]` header — for copy/export. */
  snapshot(): string {
    return `[${this.name}]\n${this.controls.map((c) => c.snapshot()).join("\n")}`;
  }
}

export class DevPanel {
  private root: HTMLDivElement;
  private list: HTMLDivElement;
  private banks: DevBank[] = [];

  constructor(container: HTMLElement, title = "⚙ DEV CONTROLS") {
    const root = document.createElement("div");
    root.style.cssText = "position:fixed;right:12px;top:12px;width:258px;z-index:60;background:rgba(10,14,18,0.92);border:1px solid #234;border-radius:8px;color:#dfe3ea;font:12px/1.4 'Courier New',monospace;user-select:none;pointer-events:auto;box-shadow:0 6px 22px rgba(0,0,0,0.4)";

    const bar = document.createElement("div");
    bar.style.cssText = "display:flex;align-items:center;gap:8px;padding:8px 10px;cursor:pointer;color:#3fd0e6;letter-spacing:0.08em;font-size:11px;text-transform:uppercase";
    const tEl = document.createElement("span");
    tEl.textContent = title;
    tEl.style.flex = "1";
    const copyAll = copyButton(() => this.snapshotAll()); // export every section at once
    const caret = document.createElement("span");
    caret.textContent = "▾";
    bar.append(tEl, copyAll, caret);
    root.appendChild(bar);

    const list = document.createElement("div");
    list.style.cssText = "max-height:calc(100vh - 60px);overflow-y:auto;padding:0 10px 10px";
    root.appendChild(list);
    this.list = list;

    const openKey = `${LS_PREFIX}__panel_open`;
    let open = lsGet(openKey) !== "0";
    const applyOpen = (): void => { list.style.display = open ? "block" : "none"; caret.textContent = open ? "▾" : "▸"; };
    applyOpen();
    bar.addEventListener("click", () => { open = !open; lsSet(openKey, open ? "1" : "0"); applyOpen(); });

    container.appendChild(root);
    this.root = root;
  }

  /** Add a collapsible category. `openByDefault` sets the first-run state (persists after). */
  addBank(name: string, openByDefault = true): DevBank {
    const wrap = document.createElement("div");
    wrap.style.cssText = "margin-top:8px;border-top:1px solid #1c2833;padding-top:6px";

    const header = document.createElement("div");
    header.style.cssText = "display:flex;align-items:center;gap:6px;cursor:pointer;color:#8fb8c4;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;padding:2px 0";
    const hName = document.createElement("span");
    hName.textContent = name;
    hName.style.flex = "1";
    const body = document.createElement("div");
    const bank = new DevBank(name, body);
    const copy = copyButton(() => bank.snapshot()); // copy THIS section's values (works even when collapsed)
    const hCaret = document.createElement("span");
    header.append(hName, copy, hCaret);

    const openKey = `${LS_PREFIX}open:${name}`;
    const s = lsGet(openKey);
    let open = s == null ? openByDefault : s !== "0";
    const applyOpen = (): void => { body.style.display = open ? "block" : "none"; hCaret.textContent = open ? "–" : "+"; };
    applyOpen();
    header.addEventListener("click", () => { open = !open; lsSet(openKey, open ? "1" : "0"); applyOpen(); });

    wrap.append(header, body);
    this.list.appendChild(wrap);
    this.banks.push(bank);
    return bank;
  }

  /** Re-push every slider's current value (call after a slider target is rebuilt). */
  applyAll(): void { for (const b of this.banks) b.applyAll(); }
  /** Every section's values, for copy/export. */
  snapshotAll(): string { return this.banks.map((b) => b.snapshot()).join("\n\n"); }
  setVisible(v: boolean): void { this.root.style.display = v ? "block" : "none"; }
}

function clamp(v: number, lo: number, hi: number): number { return v < lo ? lo : v > hi ? hi : v; }
function round(v: number): number { return Math.round(v * 1000) / 1000; }
